import { useState } from "react";
import { Save, FolderOpen, FilePlus, Trash2, Pencil, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import type { SavedSketch } from "@/hooks/use-sketch-state";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

interface Props {
  sketchList: SavedSketch[];
  activeSketchId: string | null;
  hasContent: boolean;
  saveAsNewSketch: (name: string) => string;
  loadSketch: (id: string) => void;
  deleteSketch: (id: string) => void;
  renameSketch: (id: string, name: string) => void;
  newBlankSketch: () => void;
}

const SketchManager = ({
  sketchList, activeSketchId, hasContent,
  saveAsNewSketch, loadSketch, deleteSketch, renameSketch, newBlankSketch,
}: Props) => {
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");

  const activeSketch = sketchList.find(s => s.id === activeSketchId);

  const handleSave = () => {
    const name = newName.trim();
    if (!name) return;
    saveAsNewSketch(name);
    setNewName("");
    setSaveDialogOpen(false);
    toast.success(`Saved "${name}"`);
  };

  const handleLoad = (id: string) => {
    loadSketch(id);
    setLoadDialogOpen(false);
    const s = sketchList.find(sk => sk.id === id);
    toast.success(`Loaded "${s?.name}"`);
  };

  const handleDelete = (id: string) => {
    const s = sketchList.find(sk => sk.id === id);
    deleteSketch(id);
    toast.success(`Deleted "${s?.name}"`);
  };

  const handleRename = (id: string) => {
    const name = editName.trim();
    if (!name) return;
    renameSketch(id, name);
    setEditingId(null);
    toast.success("Renamed");
  };

  const handleNew = () => {
    newBlankSketch();
    setLoadDialogOpen(false);
    toast.success("New blank sketch");
  };

  return (
    <div className="flex items-center gap-2">
      {/* Active sketch indicator */}
      {activeSketch && (
        <Badge variant="secondary" className="text-xs truncate max-w-[120px]">
          {activeSketch.name}
        </Badge>
      )}

      {/* Save button */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogTrigger asChild>
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5" disabled={!hasContent}>
            <Save className="w-3.5 h-3.5" />
            Save As
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base">Save Sketch</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="e.g. Building A - 2nd Floor"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSave()}
              autoFocus
            />
            <Button onClick={handleSave} disabled={!newName.trim()} className="w-full">
              Save Sketch
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Load button */}
      <Dialog open={loadDialogOpen} onOpenChange={setLoadDialogOpen}>
        <DialogTrigger asChild>
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5">
            <FolderOpen className="w-3.5 h-3.5" />
            Open
            {sketchList.length > 0 && (
              <Badge variant="secondary" className="text-[10px] ml-0.5 px-1">{sketchList.length}</Badge>
            )}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-md max-h-[70vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-base">Saved Sketches</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto space-y-2">
            {/* New blank */}
            <button
              onClick={handleNew}
              className="w-full flex items-center gap-3 p-3 rounded-lg border border-dashed border-border hover:bg-secondary/50 transition-colors"
            >
              <FilePlus className="w-5 h-5 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">New blank sketch</span>
            </button>

            {sketchList.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-6">
                No saved sketches yet. Draw something and hit Save As.
              </p>
            )}

            {sketchList.map(sketch => (
              <div
                key={sketch.id}
                className={`flex items-center gap-2 p-3 rounded-lg border transition-colors ${
                  sketch.id === activeSketchId
                    ? "border-primary bg-primary/5"
                    : "border-border hover:bg-secondary/50"
                }`}
              >
                <button
                  onClick={() => handleLoad(sketch.id)}
                  className="flex-1 text-left min-w-0"
                >
                  {editingId === sketch.id ? (
                    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                      <Input
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && handleRename(sketch.id)}
                        className="h-7 text-sm"
                        autoFocus
                      />
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleRename(sketch.id)}>
                        <Check className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditingId(null)}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="text-sm font-medium text-foreground truncate">{sketch.name}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {sketch.data.segments.length} run{sketch.data.segments.length !== 1 ? "s" : ""}
                        {" · "}
                        {sketch.data.fittings.length} fitting{sketch.data.fittings.length !== 1 ? "s" : ""}
                        {" · "}
                        {formatDistanceToNow(new Date(sketch.updatedAt), { addSuffix: true })}
                      </div>
                    </>
                  )}
                </button>
                {editingId !== sketch.id && (
                  <div className="flex items-center gap-0.5 shrink-0">
                    <Button
                      size="sm" variant="ghost" className="h-7 w-7 p-0"
                      onClick={e => { e.stopPropagation(); setEditingId(sketch.id); setEditName(sketch.name); }}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive"
                      onClick={e => { e.stopPropagation(); handleDelete(sketch.id); }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SketchManager;
